import {
  Grid,
  makeStyles,
  Typography,
  TypographyProps,
} from '@material-ui/core';
import { WbTooltip } from '../WbTooltip';
import { format, parseISO } from 'date-fns';
import { CSSProperties, ReactNode } from 'react';
import { WbFieldLabel } from '../WbFieldLabel';
import { WbIcon, WbIconDefinition } from '../WbIcon';
import { LinkWrapper } from './LinkWrapper';
import { WbShowMoreElement } from '../WbShowMoreElement';
import clsx from 'clsx';

const useStyle = makeStyles(theme => ({
  text: {
    overflow: 'hidden',
    fontSize: theme.typography.fontSize,
    wordBreak: 'break-word',
  },
  label: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    lineHeight: '14px',
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  wordBreak: {
    wordBreak: 'break-word',
    display: 'flex',
    alignItems: 'start',
    minWidth: 0,
  },
  span: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  textWithIconWrapper: {
    paddingTop: 2,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
  },
  // this wraps the icon in order to align it vertically with the first line of text
  iconAligner: {
    width: '1lh',
    height: '1lh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export type GenericFieldProps = {
  label?: string;
  value?: ReactNode;
  /**
   * supported types are:
   * 'string' | 'date'
   */
  type?: string;
  href?: string;
  hrefTarget?: string;
  textVariant?: 'body1' | 'body2';
  showTooltip?: boolean;
  icon?: WbIconDefinition;
  maxLines?: number;
  gridItem?: boolean;
  gridSizes?: Record<string, number>;
  contentProps?: TypographyProps;
  labelStyle?: CSSProperties;
  showMoreButton?: boolean;
  dateFormat?: string;
  iconAlignment?: 'left' | 'right';
  labelInfo?: string;
};

const IconWrapper = ({
  icon,
  contentProps,
  textVariant,
  children,
  iconAlignment,
}: {
  icon?: WbIconDefinition;
  contentProps?: TypographyProps;
  textVariant?: 'body1' | 'body2';
  children?: ReactNode;
  iconAlignment?: 'left' | 'right';
}) => {
  const classes = useStyle();

  const content = (
    <div className={classes.textWithIconWrapper}>
      {!!icon && (!iconAlignment || iconAlignment === 'left') && (
        <div className={classes.iconAligner}>
          <WbIcon icon={icon} />
        </div>
      )}
      <Typography
        variant={textVariant}
        className={clsx(classes.wordBreak)}
        {...contentProps}
      >
        {children}
      </Typography>
      {!!icon && iconAlignment === 'right' && <WbIcon icon={icon} />}
    </div>
  );

  return content;
};

const Content = ({
  value,
  type,
  href,
  hrefTarget,
  maxLines,
  icon,
  textVariant,
  contentProps,
  dateFormat = 'yyyy/MM/dd HH:mm:ss',
  showMoreButton = false,
  showTooltip,
  iconAlignment,
}: Omit<GenericFieldProps, 'label'>) => {
  let displayValue: string;

  const classes = useStyle();

  const iconWrapperProps = {
    icon,
    contentProps,
    textVariant,
    iconAlignment,
  };

  if (typeof value === 'string') {
    displayValue = value;
    if (type === 'date') {
      try {
        displayValue = format(parseISO(value as string), dateFormat);
      } catch (e) {
        displayValue = value;
      }
    }
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    displayValue = value.toString();
  } else if (!value && !!icon) {
    return <IconWrapper {...iconWrapperProps} />;
  } else if (!value) {
    displayValue = '-';
  } else {
    // this is the case when value is a ReactNode
    return <>{value}</>;
  }

  if (href)
    return (
      <IconWrapper {...iconWrapperProps}>
        <LinkWrapper
          value={displayValue}
          href={href}
          hrefTarget={hrefTarget}
          showTooltip
        />
      </IconWrapper>
    );

  if ((maxLines === undefined || maxLines === -1) && !showMoreButton)
    return (
      <IconWrapper {...iconWrapperProps}>
        {showTooltip ? (
          <WbTooltip title={displayValue}>
            <span className={classes.span}>{displayValue}</span>
          </WbTooltip>
        ) : (
          displayValue
        )}
      </IconWrapper>
    );

  // in this case, tooltip is handled by WbShowMoreElement
  return (
    <IconWrapper {...iconWrapperProps}>
      <span>
        <WbShowMoreElement maxLines={maxLines} showMoreButton={showMoreButton}>
          {displayValue}
        </WbShowMoreElement>
      </span>
    </IconWrapper>
  );
};

export const GenericField = ({
  label,
  gridSizes,
  gridItem = true,
  labelStyle,
  labelInfo,
  ...content
}: GenericFieldProps) => {
  const wrappedContent = (
    <>
      {label && (
        <WbFieldLabel label={label} style={labelStyle} labelInfo={labelInfo} />
      )}
      <Content {...content} />
    </>
  );

  if (gridItem) {
    return (
      <Grid item {...gridSizes}>
        {wrappedContent}
      </Grid>
    );
  }

  return wrappedContent;
};
